/**
 * CRM ダッシュボード API hooks
 */

import { restClient } from "lib/rest-client";
import { useCallback, useEffect, useRef, useState } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DashboardAlert = {
  type: string;
  severity: "critical" | "warning" | "info";
  message: string;
  link: string;
  talentId: string;
  talentName: string;
  metadata: Record<string, unknown>;
};

export type DashboardSummaryStats = {
  totalTalents: number;
  workingTalents: number;
  availableTalents: number;
  utilizationRate: number;
  activePipelines: number;
  wonThisMonth: number;
  lostThisMonth: number;
  winRate: number;
};

export type PipelineFunnelStage = {
  stage: string;
  label: string;
  count: number;
};

export type ExpiringContract = {
  contractId: string;
  talentId: string;
  talentName: string;
  clientCompany: string;
  endDate: string;
  remainingDays: number;
  urgency: "critical" | "warning" | "normal";
};

export type DashboardSummaryResponse = {
  alerts: DashboardAlert[];
  summary: DashboardSummaryStats;
  pipelineFunnel: PipelineFunnelStage[];
  expiringContracts: ExpiringContract[];
};

export type UtilizationHistoryEntry = {
  month: string;
  rate: number;
  working: number;
  total: number;
};

export type TopSkill = {
  skillName: string;
  count: number;
};

export type Financials = {
  averageReward: number;
  averageMargin: number;
};

export type AssigneePerformance = {
  userId: string;
  userName: string;
  activePipelines: number;
  wonCount: number;
  lostCount: number;
  talentCount: number;
  workingCount: number;
};

export type DashboardAnalyticsResponse = {
  utilizationHistory: UtilizationHistoryEntry[];
  topSkills: TopSkill[];
  financials: Financials | null;
  assigneePerformance: AssigneePerformance[] | null;
};

// ---------------------------------------------------------------------------
// Generic fetch state
// ---------------------------------------------------------------------------

type FetchState<T> = {
  data: T | null;
  loading: boolean;
  error: Error | null;
};

// ---------------------------------------------------------------------------
// useDashboardSummary
// ---------------------------------------------------------------------------

/**
 * ダッシュボードサマリーを取得する hook
 * @return {object} data, loading, error, refetch
 */
export function useDashboardSummary(): FetchState<DashboardSummaryResponse> & {
  refetch: () => void;
} {
  const [state, setState] = useState<FetchState<DashboardSummaryResponse>>({
    data: null,
    loading: true,
    error: null,
  });
  const mountedRef = useRef(true);

  const fetchData = useCallback(() => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    restClient
      .get<DashboardSummaryResponse>("/crm/dashboard/summary")
      .then(data => {
        if (mountedRef.current) {
          setState({ data, loading: false, error: null });
        }
      })
      .catch((err: unknown) => {
        if (mountedRef.current) {
          setState({
            data: null,
            loading: false,
            error: err instanceof Error ? err : new Error(String(err)),
          });
        }
      });
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchData();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchData]);

  return { ...state, refetch: fetchData };
}

// ---------------------------------------------------------------------------
// useDashboardAnalytics
// ---------------------------------------------------------------------------

/**
 * ダッシュボード分析データを取得する hook（呼び出し時のみフェッチ）
 * @return {object} data, loading, error, refetch
 */
export function useDashboardAnalytics(): FetchState<DashboardAnalyticsResponse> & {
  refetch: () => void;
} {
  const [state, setState] = useState<FetchState<DashboardAnalyticsResponse>>({
    data: null,
    loading: false,
    error: null,
  });
  const mountedRef = useRef(true);

  const fetchData = useCallback(() => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    restClient
      .get<DashboardAnalyticsResponse>("/crm/dashboard/analytics")
      .then(data => {
        if (mountedRef.current) {
          setState({ data, loading: false, error: null });
        }
      })
      .catch((err: unknown) => {
        if (mountedRef.current) {
          setState({
            data: null,
            loading: false,
            error: err instanceof Error ? err : new Error(String(err)),
          });
        }
      });
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return { ...state, refetch: fetchData };
}
